import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AdminAuthProvider } from "@/context/AdminAuthContext";
import Layout from "@/components/layout/Layout";
import ProtectedRoute from "@/components/portal/ProtectedRoute";
import PortalShell from "@/components/portal/PortalShell";
import AdminProtected from "@/components/admin/AdminProtected";
import AdminLayout from "@/components/admin/AdminLayout";

// Marketing pages (Phase 1)
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import ContactPage from "@/pages/ContactPage";
import NotFoundPage from "@/pages/NotFoundPage";
import VerifyPage from "@/pages/VerifyPage";

// Careers (Phase 3, public)
import CareersPage from "@/pages/CareersPage";
import CareerDetailPage from "@/pages/CareerDetailPage";

// Portal pages (Phase 2)
import PortalLandingPage from "@/pages/portal/PortalLandingPage";
import PortalLoginPage from "@/pages/portal/PortalLoginPage";
import PortalSignupPage from "@/pages/portal/PortalSignupPage";
import CoursesPage from "@/pages/portal/CoursesPage";
import CourseDetailPage from "@/pages/portal/CourseDetailPage";
import LessonViewerPage from "@/pages/portal/LessonViewerPage";
import DashboardPage from "@/pages/portal/DashboardPage";
import CertificatesPage from "@/pages/portal/CertificatesPage";
import CertificateDetailPage from "@/pages/portal/CertificateDetailPage";

// Admin pages (Phase 3)
import AdminLoginPage from "@/pages/admin/AdminLoginPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminLeadsPage from "@/pages/admin/AdminLeadsPage";
import AdminTraineesPage from "@/pages/admin/AdminTraineesPage";
import AdminCoursesPage from "@/pages/admin/AdminCoursesPage";
import AdminCertificatesPage from "@/pages/admin/AdminCertificatesPage";
import AdminJobsPage from "@/pages/admin/AdminJobsPage";
import AdminJobFormPage from "@/pages/admin/AdminJobFormPage";
import AdminApplicationsPage from "@/pages/admin/AdminApplicationsPage";
import AdminApplicationDetailPage from "@/pages/admin/AdminApplicationDetailPage";

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <HelmetProvider>
                    <AuthProvider>
                        <AdminAuthProvider>
                        <Routes>
                            {/* Admin routes — separate shell, no marketing header/footer */}
                            <Route path="/admin/login" element={<AdminLoginPage />} />
                            <Route
                                path="/admin"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminDashboardPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/leads"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminLeadsPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/trainees"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminTraineesPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/courses"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminCoursesPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/certificates"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminCertificatesPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/jobs"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminJobsPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/jobs/new"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminJobFormPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/jobs/:id/edit"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminJobFormPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/applications"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminApplicationsPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />
                            <Route
                                path="/admin/applications/:id"
                                element={
                                    <AdminProtected>
                                        <AdminLayout>
                                            <AdminApplicationDetailPage />
                                        </AdminLayout>
                                    </AdminProtected>
                                }
                            />

                            {/* Public site — everything else wrapped in Layout (marketing header + footer) */}
                            <Route
                                path="/*"
                                element={
                                    <Layout>
                                        <Routes>
                                            {/* Marketing */}
                                            <Route path="/" element={<HomePage />} />
                                            <Route path="/about" element={<AboutPage />} />
                                            <Route path="/services" element={<ServicesPage />} />
                                            <Route path="/projects" element={<ProjectsPage />} />
                                            <Route path="/projects/:slug" element={<ProjectDetailPage />} />
                                            <Route path="/contact" element={<ContactPage />} />

                                            {/* Careers (Phase 3) */}
                                            <Route path="/careers" element={<CareersPage />} />
                                            <Route path="/careers/:slug" element={<CareerDetailPage />} />

                                            {/* Public verification */}
                                            <Route path="/verify/:slug" element={<VerifyPage />} />

                                            {/* Portal — public entry points */}
                                            <Route
                                                path="/portal"
                                                element={
                                                    <PortalShell>
                                                        <PortalLandingPage />
                                                    </PortalShell>
                                                }
                                            />
                                            <Route path="/portal/login" element={<PortalLoginPage />} />
                                            <Route path="/portal/signup" element={<PortalSignupPage />} />

                                            {/* Portal — public browse */}
                                            <Route
                                                path="/portal/courses"
                                                element={
                                                    <PortalShell>
                                                        <CoursesPage />
                                                    </PortalShell>
                                                }
                                            />
                                            <Route
                                                path="/portal/courses/:slug"
                                                element={
                                                    <PortalShell>
                                                        <CourseDetailPage />
                                                    </PortalShell>
                                                }
                                            />

                                            {/* Portal — authenticated */}
                                            <Route
                                                path="/portal/dashboard"
                                                element={
                                                    <ProtectedRoute>
                                                        <PortalShell>
                                                            <DashboardPage />
                                                        </PortalShell>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/portal/learn/:courseSlug/:lessonId"
                                                element={
                                                    <ProtectedRoute>
                                                        <LessonViewerPage />
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/portal/certificates"
                                                element={
                                                    <ProtectedRoute>
                                                        <PortalShell>
                                                            <CertificatesPage />
                                                        </PortalShell>
                                                    </ProtectedRoute>
                                                }
                                            />
                                            <Route
                                                path="/portal/certificates/:certId"
                                                element={
                                                    <ProtectedRoute>
                                                        <PortalShell>
                                                            <CertificateDetailPage />
                                                        </PortalShell>
                                                    </ProtectedRoute>
                                                }
                                            />

                                            <Route path="*" element={<NotFoundPage />} />
                                        </Routes>
                                    </Layout>
                                }
                            />
                        </Routes>
                        <Toaster
                            position="bottom-right"
                            theme="dark"
                            toastOptions={{
                                style: {
                                    background: "#1a1a1a",
                                    color: "#F5F5F4",
                                    border: "1px solid #B8892C",
                                    borderRadius: 0,
                                    fontFamily: "Inter, sans-serif",
                                },
                            }}
                        />
                    </AdminAuthProvider>
                    </AuthProvider>
                </HelmetProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
